import { Button } from "@/project/karuna/components/ui/button"; // @ts-ignore - button is not a valid module
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/project/karuna/components/ui/card"; // @ts-ignore - card is not a valid module
import {
  Heart,
  Users,
  Award,
  BookOpen,
  Globe,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { Link } from "react-router-dom"; // @ts-ignore - react-router-dom is not a valid module
import PublicLayout from "./PublicLayout";
import navigationRoutes, {
  useKarunaLink,
} from "@/project/karuna/utils/navigationRoutes";

const Home = () => {
  const getLink = useKarunaLink();
  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="relative flex min-h-[700px] w-full items-center justify-center overflow-hidden bg-primary text-white">
        <div className="relative z-10 mx-auto max-w-5xl px-4 py-20 text-center">
          <div className="animate-fade-in space-y-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm backdrop-blur-sm">
              <Sparkles className="h-4 w-4" />
              <span>Transforming Lives Through Compassion</span>
            </div>

            <h1 className="mb-6 text-5xl font-bold leading-tight md:text-7xl">
              Building A World of
              <span className="block">
                <span className="text-secondary">Kindness</span> & Care
              </span>
            </h1>

            <p className="mx-auto mb-8 max-w-3xl text-xl text-white/90 md:text-2xl">
              Awakening compassion in young hearts. Empowering 2,500+ schools
              across India to create a better tomorrow.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4">
              <Button
                className="group bg-white text-primary shadow-lg hover:bg-white/90"
                asChild
              >
                <Link to={getLink(navigationRoutes.DEMO_LOGIN)}>
                  Get Started
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
              <Button
                className="border-white/30 text-white backdrop-blur-sm hover:bg-white/10"
                asChild
              >
                <Link to={getLink(navigationRoutes.PROGRAMS)}>
                  Explore Programs
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="flex h-10 w-6 items-start justify-center rounded-full border-2 border-white/30 p-2">
            <div className="h-3 w-1 rounded-full bg-white/50" />
          </div>
        </div>
      </section>

      {/* Action Cards */}
      <section className="bg-background py-16">
        <div className="container mx-auto px-4">
          <div className="relative z-20 -mt-32 grid gap-6 md:grid-cols-2">
            <Card className="shadow-strong transition-shadow hover:shadow-natural">
              <CardHeader>
                <Heart className="mb-4 h-12 w-12 text-primary" />
                <CardTitle>Donation</CardTitle>
                <CardDescription>Donate for a better world</CardDescription>
              </CardHeader>
              <CardContent>
                <Button className="w-full" asChild>
                  <Link to={getLink(navigationRoutes.DONATE)}>Donate Now</Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="shadow-strong transition-shadow hover:shadow-natural">
              <CardHeader>
                <Users className="mb-4 h-12 w-12 text-secondary" />
                <CardTitle>Volunteer</CardTitle>
                <CardDescription>Join the Karuna Movement</CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90"
                  asChild
                >
                  <Link to={getLink(navigationRoutes.CONTACT)}>Join Now</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="bg-muted/30 py-20">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-4xl font-bold text-foreground">
              About Us
            </h2>
            <div className="mx-auto mb-6 h-1 w-24 bg-primary" />
            <p className="mx-auto max-w-3xl text-xl text-muted-foreground">
              Become a part of a better world
            </p>
          </div>
          <div className="mx-auto max-w-4xl text-center">
            <p className="text-lg leading-relaxed text-foreground/80">
              Karuna International is a registered nonprofit service
              organization dedicated to awakening love, kindness, and compassion
              in children and young adults towards all living beings. We inspire
              appreciation for the environment and encourage conservation
              efforts in alignment with constitutional values.
            </p>
            <Button className="mt-8" asChild>
              <Link to={getLink(navigationRoutes.ABOUT)}>Learn More</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="bg-background py-20">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-4xl font-bold text-foreground">
              Our Mission
            </h2>
            <p className="text-xl text-primary">
              Transform youngsters into global citizens
            </p>
          </div>
          <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-3">
            <Card className="shadow-medium">
              <CardHeader>
                <BookOpen className="mb-4 h-12 w-12 text-primary" />
                <CardTitle>Moral Values</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• Value education for children</li>
                  <li>• Compassion stories from epics</li>
                  <li>• Animal welfare in curriculum</li>
                  <li>• Violence-free science education</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="shadow-medium">
              <CardHeader>
                <Heart className="mb-4 h-12 w-12 text-secondary" />
                <CardTitle>Kindness & Compassion</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• Compassion to animals</li>
                  <li>• Preventing cruelty</li>
                  <li>• Compassionate living</li>
                  <li>• Beauty without cruelty</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="shadow-medium">
              <CardHeader>
                <Globe className="mb-4 h-12 w-12 text-accent" />
                <CardTitle>Animal Welfare</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• Environmental conservation</li>
                  <li>• Wildlife protection</li>
                  <li>• Ethical treatment of animals</li>
                  <li>• Sustainable practices</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-secondary py-20 text-white">
        <div className="container mx-auto px-4">
          <div className="grid gap-8 text-center md:grid-cols-4">
            <div>
              <Award className="mx-auto mb-4 h-16 w-16" />
              <h3 className="mb-2 text-4xl font-bold">2850+</h3>
              <p className="text-lg">Schools</p>
            </div>
            <div>
              <Users className="mx-auto mb-4 h-16 w-16" />
              <h3 className="mb-2 text-4xl font-bold">18+ Lakh</h3>
              <p className="text-lg">Students</p>
            </div>
            <div>
              <BookOpen className="mx-auto mb-4 h-16 w-16" />
              <h3 className="mb-2 text-4xl font-bold">15000+</h3>
              <p className="text-lg">Activities</p>
            </div>
            <div>
              <Heart className="mx-auto mb-4 h-16 w-16" />
              <h3 className="mb-2 text-4xl font-bold">25+</h3>
              <p className="text-lg">Years</p>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default Home;
